import type { ReactNode } from "react";
import { clsx } from "clsx";
import { Heading, Text } from "../typography";

export interface AccentStatCardProps {
  label: string;
  /** Usually a string; a node lets a price be wrapped in `<GatedPrice>`. */
  value: ReactNode;
  accent?: "green" | "amber" | "cyan";
  /** Renders without the coloured accent bar — plain label/value card. */
  bare?: boolean;
  className?: string;
  onClick?: () => void;
}

export function AccentStatCard({ label, value, accent = "green", bare = false, className, onClick }: AccentStatCardProps) {
  const interactive = !!onClick;

  if (bare) {
    const classes = clsx(
      "flex flex-col gap-1 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 text-left",
      "shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
      interactive &&
        "cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:-translate-y-0.5 focus-visible:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan/40",
      className,
    );

    const content = (
      <>
        <Text variant="bodySm" tone="muted">{label}</Text>
        <Heading variant="h3" tone="dark">{value}</Heading>
      </>
    );

    if (interactive) {
      return (
        <button type="button" onClick={onClick} className={clsx("group", classes)}>
          {content}
        </button>
      );
    }

    return <div className={classes}>{content}</div>;
  }

  const classes = clsx(
    "rounded-2xl border border-borderDefault bg-bgWhite px-5 py-4 text-left",
    "shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
    interactive &&
      "cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:-translate-y-0.5 focus-visible:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan/40",
    className,
  );

  const content = (
    <div className="flex items-stretch gap-3">
      <div
        className={clsx(
          "w-1 shrink-0 rounded-full",
          accent === "amber" ? "bg-accentAmber" : accent === "cyan" ? "bg-accentCyan" : "bg-accentGreen",
        )}
      />
      <div className="flex flex-col justify-between gap-6">
        <Text variant="bodySm" tone="muted">{label}</Text>
        <Heading variant="h3" tone="dark">{value}</Heading>
      </div>
    </div>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={clsx("group", classes)}>
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}
